import { Button, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ScheduleUpdateScope } from '../../types/MachineDetailsRobotSchedule.types';
import { useMachineDetailsRobotSchedule } from '../../context/MachineDetailsRobotScheduleContext';
import {
  IconContainer,
  ScheduleUpdateConfirmModalContainer,
  UpdateScopeOptions,
} from './ScheduleUpdateConfirmModal.styles';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { Radio } from 'lib/components/Radio/Radio';
import { selectUpdateScheduleIsLoading } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotSchedule/robotScheduleSelectors';

interface ScheduleUpdateConfirmModalProps {
  isB50: boolean;
  handleConfirmUpdate: () => void;
}

export const ScheduleUpdateConfirmModal = ({
  isB50,
  handleConfirmUpdate,
}: ScheduleUpdateConfirmModalProps): JSX.Element => {
  const { t } = useTranslation();
  const isUpdateScheduleLoading = useSelector(selectUpdateScheduleIsLoading);

  const { isUpdateConfirmModalOpen, handleCloseUpdateConfirmModal, updateScope, handleUpdateScopeChange } =
    useMachineDetailsRobotSchedule();

  const b50 = {
    title: t('scheduleUpdateConfirmModal.title'),
    description: t('scheduleUpdateConfirmModal.description'),
    updateScope: () => (
      <UpdateScopeOptions>
        <Radio.Group
          value={updateScope}
          onChange={(event): void => handleUpdateScopeChange(event.target.value as ScheduleUpdateScope)}
          disabled={isUpdateScheduleLoading}
        >
          <Flex vertical gap={kccThemeConstants.SPACING.MD}>
            <Radio value={ScheduleUpdateScope.ThisEventOnly}>{t('scheduleUpdateConfirmModal.thisEventOnly')}</Radio>
            <Radio value={ScheduleUpdateScope.ThisAndFollowing}>
              {t('scheduleUpdateConfirmModal.thisAndFollowingEvents')}
            </Radio>
          </Flex>
        </Radio.Group>
      </UpdateScopeOptions>
    ),
  };

  const cv50 = {
    title: t('scheduleUpdateConfirmModal.cv50Title'),
    description: t('scheduleUpdateConfirmModal.cv50Description'),
    updateScope: () => <></>,
  };

  const renderRelevantContent = (): { title: string; description: string; updateScope: () => JSX.Element } =>
    isB50 ? b50 : cv50;

  return (
    <ScheduleUpdateConfirmModalContainer
      title={
        <Flex align="center" justify="space-between">
          {renderRelevantContent().title}

          <Button
            type="link"
            aria-label={t('common.close')}
            onClick={handleCloseUpdateConfirmModal}
            disabled={isUpdateScheduleLoading}
            style={{ width: kccThemeConstants.SIZE.S300, height: kccThemeConstants.SIZE.S300, padding: 0 }}
          >
            <IconContainer>
              <SvgIcon name="close" />
            </IconContainer>
          </Button>
        </Flex>
      }
      open={isUpdateConfirmModalOpen}
      onCancel={handleCloseUpdateConfirmModal}
      centered
      closable={false}
      destroyOnClose
      footer={[
        <Button
          key="schedule-update-cancel"
          onClick={handleCloseUpdateConfirmModal}
          disabled={isUpdateScheduleLoading}
          style={{ minWidth: '100px', width: 'fit-content' }}
        >
          {t('common.cancel')}
        </Button>,
        <Button
          key="schedule-update-confirm"
          type="primary"
          onClick={handleConfirmUpdate}
          loading={isUpdateScheduleLoading}
          style={{ minWidth: '100px', width: 'fit-content' }}
        >
          {t('common.confirm')}
        </Button>,
      ]}
    >
      <Typography.Text>{renderRelevantContent().description}</Typography.Text>

      {renderRelevantContent().updateScope()}
    </ScheduleUpdateConfirmModalContainer>
  );
};
